import { ApolloClient } from '@apollo/client';
import { includes, isEmpty, orderBy } from 'lodash';

import {
  AccessParameterKind,
  BeneficiaryKind,
  BeneficiaryLevel,
  ScheduledDistributionFrequency,
  ScheduledDistributionKind,
} from '@/types/schema';
import * as diagnostics from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';
import { getNodes } from '@/utils/graphqlUtils';

import { EntityType } from '../types/EntityType';
import {
  SURVIVING_SPOUSE_SENTINEL,
  validUnitrustTypesV2,
} from './EntityBeneficiariesSubform.constants';
import {
  defaultBeneficiary,
  defaultCharitableBeneficiary,
} from './EntityBeneficiariesSubform.defaults';
import {
  AccessParameterRadioGroupKind,
  BeneficiaryFormAccessParameter,
  BeneficiaryFormAccessParameterAgeParam,
  BeneficiaryFormAccessParameters,
  BeneficiaryFormPowerOfAppointment,
  DistributionType,
  EntityBeneficiariesFormBeneficiary,
  EntityBeneficiariesFormNamespace,
  EntityBeneficiariesFormVariant,
  EntityBeneficiariesSubformShape,
  EntityBeneficiaryAccessFrequencies,
} from './EntityBeneficiariesSubform.types';
import {
  BeneficiariesDocument,
  BeneficiariesQuery,
  BeneficiarySubform_AccessAgeParameterFragment,
  BeneficiarySubform_AccessParameterFragment,
  BeneficiarySubform_BeneficiaryFragment,
  BeneficiarySubform_ScheduledDistributionFragment,
} from './graphql/Beneficiaries.generated';

function getFormBeneficiaryId(
  beneficiary: BeneficiarySubform_BeneficiaryFragment
): string {
  // SURVIVING_SPOUSE is a dynamic placeholder that doesn't link to a specific individual.
  // It's only used in subtrust (testamentary entity) beneficiary forms for two-grantor
  // households and resolves to whichever spouse survives at runtime.
  if (beneficiary.kind === BeneficiaryKind.SurvivingSpouse) {
    return SURVIVING_SPOUSE_SENTINEL;
  }

  if (beneficiary.individual) {
    return beneficiary.individual.id;
  }

  if (beneficiary.entity) {
    return beneficiary.entity.id;
  }

  if (beneficiary.organization) {
    return beneficiary.organization.id;
  }

  if (beneficiary.testamentaryEntity) {
    return beneficiary.testamentaryEntity.id;
  }

  return '';
}

function getPowerOfAppointment(
  beneficiary: BeneficiarySubform_BeneficiaryFragment
): BeneficiaryFormPowerOfAppointment {
  if (beneficiary.powerOfAppointment) {
    return {
      _hasPowerOfAppointment: !!beneficiary.powerOfAppointment.power,
      power: beneficiary.powerOfAppointment.power ?? '',
      powerOtherNote: beneficiary.powerOfAppointment.powerOtherNote ?? '',
    };
  }

  return {
    _hasPowerOfAppointment: false,
    power: '',
  };
}

function getAccessParameterWithKind(
  accessParameter: BeneficiaryFormAccessParameter
): BeneficiaryFormAccessParameter {
  const kind = accessParameter.amount
    ? AccessParameterKind.Amount
    : AccessParameterKind.Percentage;
  return {
    ...accessParameter,
    kind,
  };
}

function getAgeRequirementKind(
  ageParam: Pick<
    BeneficiarySubform_AccessAgeParameterFragment,
    'ageRequirementStart' | 'ageRequirementEnd'
  >
): BeneficiaryFormAccessParameterAgeParam['_ageRequirementKind'] {
  if (ageParam.ageRequirementStart && ageParam.ageRequirementEnd) {
    return 'between';
  }

  if (ageParam.ageRequirementEnd) return 'until';

  return 'upon';
}

export function getAccessParameterKindFromSchedculedDistributionKind(
  input: ScheduledDistributionKind | null | undefined
): AccessParameterKind | null {
  switch (input) {
    case ScheduledDistributionKind.Amount:
      return AccessParameterKind.Amount;
    case ScheduledDistributionKind.Percentage:
      return AccessParameterKind.Percentage;
    case ScheduledDistributionKind.AllIncome:
      return AccessParameterKind.AllTrustIncome;
    default:
      return null;
  }
}

export function mapScheduleDistributionToAccessParameter(
  scheduledDistribution: BeneficiarySubform_ScheduledDistributionFragment
): BeneficiaryFormAccessParameter | null {
  const kind = getAccessParameterKindFromSchedculedDistributionKind(
    scheduledDistribution.kind
  );
  if (!kind) return null;

  const mappedDistribution: BeneficiaryFormAccessParameter = {
    kind,
    _hasFrequency:
      scheduledDistribution.frequency === ScheduledDistributionFrequency.OneTime
        ? false
        : true,
    frequency: scheduledDistribution.frequency ?? undefined,
    growByInflation: scheduledDistribution.growByInflation ?? false,
    _hasAmountOrPercentage:
      !!scheduledDistribution.amount || !!scheduledDistribution.percentage,
    amount: (scheduledDistribution.amount as unknown as undefined) ?? undefined,
    percentage:
      (scheduledDistribution.percentage as unknown as undefined) ?? undefined,
    distributionType: DistributionType.Scheduled,
    _hasAgeParams:
      !!scheduledDistribution.ageRequirementEnd ||
      !!scheduledDistribution.ageRequirementStart,
    ageParams: [
      {
        _ageRequirementKind: getAgeRequirementKind(scheduledDistribution),
        ageRequirementEnd:
          scheduledDistribution.ageRequirementEnd?.toString() ?? undefined,
        ageRequirementStart:
          scheduledDistribution.ageRequirementStart?.toString() ?? undefined,
        notes: undefined,
      },
    ],
  };

  return mappedDistribution;
}

function getAccessParameters(
  accessParameters: BeneficiarySubform_AccessParameterFragment[],
  scheduledDistributions: BeneficiarySubform_ScheduledDistributionFragment[]
): BeneficiaryFormAccessParameters {
  const beneficiaryAccessParameters: BeneficiaryFormAccessParameters =
    defaultBeneficiary()
      .beneficiaryFormAccessParameters as BeneficiaryFormAccessParameters; // cast is necessary because the type allows for undefined, but our default is not undefined

  if (isEmpty(accessParameters) && isEmpty(scheduledDistributions)) {
    return beneficiaryAccessParameters;
  }

  beneficiaryAccessParameters.kind =
    accessParameters[0]?.kind ?? AccessParameterKind.Full;

  const accessParams: BeneficiaryFormAccessParameter[] = accessParameters.map(
    (ap) => ({
      // TS PERF see type definition for BeneficiaryFormAccessParameter
      amount: (ap.amount as unknown as undefined) ?? undefined,
      _hasAmountOrPercentage: !!ap.amount || !!ap.percentage,
      _hasFrequency: ap.frequency ? true : false,
      frequency: ap.frequency ?? EntityBeneficiaryAccessFrequencies.Ongoing,
      kind: ap.kind,
      // TS PERF see type definition for BeneficiaryFormAccessParameter
      percentage: (ap.percentage as unknown as undefined) ?? undefined,
      growByInflation: ap.growByInflation ?? false,
      _hasAgeParams: !!getNodes(ap.accessAgeParameters).length,
      ageParams: getNodes(ap.accessAgeParameters).map((ageParam) => ({
        _ageRequirementKind: getAgeRequirementKind(ageParam),
        ageRequirementEnd: ageParam.ageRequirementEnd?.toString() ?? undefined,
        ageRequirementStart:
          ageParam.ageRequirementStart?.toString() ?? undefined,
        notes: ageParam.notes ?? undefined,
      })),
      distributionType: DistributionType.Discretionary,
    })
  );

  const hasFullAccess = accessParams.some(
    (ap) => ap.kind === AccessParameterKind.Full
  );

  const partialAccessParams = accessParams.filter((ap) =>
    [
      AccessParameterKind.Percentage,
      AccessParameterKind.Amount,
      AccessParameterKind.NetIncome,
      AccessParameterKind.NetIncomeWithMakeup,
    ].includes(ap.kind!)
  );

  const otherAccessParams = accessParams.filter((ap) =>
    [
      AccessParameterKind.FullDiscretion,
      AccessParameterKind.Hms,
      AccessParameterKind.Hems,
      AccessParameterKind.AllTrustIncome,
      AccessParameterKind.Other,
    ].includes(ap.kind!)
  );

  if (hasFullAccess) {
    beneficiaryAccessParameters._accessParameterKind =
      AccessParameterRadioGroupKind.Full;
  } else if (
    !isEmpty(partialAccessParams) ||
    !isEmpty(otherAccessParams) ||
    !isEmpty(scheduledDistributions)
  ) {
    beneficiaryAccessParameters._accessParameterKind =
      AccessParameterRadioGroupKind.Partial;
  } else {
    // fallback to full access for UI default
    beneficiaryAccessParameters._accessParameterKind =
      AccessParameterRadioGroupKind.Full;
  }

  const amountsOrPercentages: BeneficiaryFormAccessParameter[] =
    partialAccessParams.map((ap) => ({
      ...ap,
      frequency: ap.frequency ?? EntityBeneficiaryAccessFrequencies.Ongoing,
      _hasFrequency: ap.frequency ? true : false,
    }));

  if (!isEmpty(scheduledDistributions)) {
    scheduledDistributions.forEach((sd) => {
      const mappedDistribution = mapScheduleDistributionToAccessParameter(sd);
      if (
        sd.kind === ScheduledDistributionKind.AllIncome &&
        mappedDistribution
      ) {
        beneficiaryAccessParameters.values[AccessParameterKind.AllTrustIncome] =
          getAccessParameterWithKind(mappedDistribution);
        beneficiaryAccessParameters.checkboxes[
          AccessParameterKind.AllTrustIncome
        ] = true;
      } else if (mappedDistribution) {
        amountsOrPercentages.push(mappedDistribution);
      }
    });
  }

  if (!isEmpty(amountsOrPercentages)) {
    beneficiaryAccessParameters.amountsOrPercentages = amountsOrPercentages;
    beneficiaryAccessParameters._hasAmountOrPercentage = true;
  }

  const fullDiscretionParam = accessParams.find(
    (ap) => ap.kind === AccessParameterKind.FullDiscretion
  );
  if (fullDiscretionParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.FullDiscretion] =
      getAccessParameterWithKind(fullDiscretionParam);
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.FullDiscretion] =
      true;
  }

  const hmsParam = accessParams.find(
    (ap) => ap.kind === AccessParameterKind.Hms
  );
  if (hmsParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.Hms] =
      getAccessParameterWithKind(hmsParam);
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.Hms] = true;
  }

  const hemsParam = accessParams.find(
    (ap) => ap.kind === AccessParameterKind.Hems
  );
  if (hemsParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.Hems] =
      getAccessParameterWithKind(hemsParam);
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.Hems] = true;
  }

  const trustIncomeParam = accessParams.find(
    (ap) => ap.kind === AccessParameterKind.AllTrustIncome
  );
  if (trustIncomeParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.AllTrustIncome] =
      getAccessParameterWithKind(trustIncomeParam);
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.AllTrustIncome] =
      true;
  }

  const otherParam = accessParams.find(
    (ap) => ap.kind === AccessParameterKind.Other
  );
  if (otherParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.Other] =
      getAccessParameterWithKind(otherParam);
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.Other] = true;
  }

  return beneficiaryAccessParameters;
}

export function beneficiaryFragmentToFormBeneficiary(
  beneficiary: BeneficiarySubform_BeneficiaryFragment
): EntityBeneficiariesFormBeneficiary {
  const output: EntityBeneficiariesFormBeneficiary = {
    id: beneficiary.id || null,
    beneficiaryId: getFormBeneficiaryId(beneficiary),
    level: beneficiary.level ?? '',
    notes: beneficiary.notes ?? '',
    beneficiaryFormAccessParameters: getAccessParameters(
      getNodes(beneficiary.accessParameters),
      getNodes(beneficiary.scheduledDistributions)
    ),
    beneficiaryFormScheduledDistributions: {
      _hasScheduledDistributions: !!getNodes(beneficiary.scheduledDistributions)
        .length,
      scheduledDistributions:
        getNodes(beneficiary.scheduledDistributions).map((sd) => {
          const scheduledDistribution = {
            ageRequirementEnd: sd.ageRequirementEnd?.toString() ?? undefined,
            ageRequirementStart:
              sd.ageRequirementStart?.toString() ?? undefined,
            amount: sd.amount ?? undefined,
            frequency: sd.frequency ?? ('' as const),
            growByInflation: sd.growByInflation ?? false,
            kind: sd.kind,
            percentage: sd.percentage ?? undefined,
            scheduledDistributionNotes:
              sd.scheduledDistributionNotes ?? undefined,
          };

          return {
            _hasAgeParams: Boolean(
              sd.ageRequirementStart || sd.ageRequirementEnd
            ),
            _ageRequirementKind: getAgeRequirementKind({
              ageRequirementStart: sd.ageRequirementStart,
              ageRequirementEnd: sd.ageRequirementEnd,
            }),
            scheduledDistribution,
          };
        }) ?? [],
    },
    beneficiaryFormPowerOfAppointment: getPowerOfAppointment(beneficiary),
  };

  return output;
}

function getListOfFormBeneficiaries(
  beneficiaries: BeneficiarySubform_BeneficiaryFragment[],
  beneficiaryType:
    | 'beneficiaries'
    | 'lifetimeBeneficiaries'
    | 'remainderBeneficiaries'
) {
  return {
    [beneficiaryType]:
      sortBeneficiariesByLevel(beneficiaries).map((beneficiary) =>
        beneficiaryFragmentToFormBeneficiary(beneficiary)
      ) ?? [],
  };
}

function dataToForm(data: BeneficiariesQuery): EntityBeneficiariesSubformShape {
  if (data.node?.__typename !== 'Entity') {
    throw new Error('Unexpected node type');
  }

  if (data.node.subtype.__typename === 'SLATTrust') {
    return {
      ...getListOfFormBeneficiaries(
        data.node.subtype.lifetimeBeneficiaries ?? [],
        'lifetimeBeneficiaries'
      ),
      ...getListOfFormBeneficiaries(
        data.node.subtype.remainderBeneficiaries ?? [],
        'remainderBeneficiaries'
      ),
    };
  }

  if (data.node.subtype.__typename === 'CLTTrust') {
    const lifetimeBeneficiary:
      | BeneficiarySubform_BeneficiaryFragment
      | undefined = data.node.subtype.lifetimeBeneficiaries?.[0];

    if (getNodes(lifetimeBeneficiary?.accessParameters).length > 1) {
      diagnostics.error(
        'Beneficiary count assumption violation',
        new Error(
          'CLT lifetime beneficiary has more than one access parameter'
        ),
        {
          beneficiaryId: lifetimeBeneficiary!.id,
        }
      );
    }

    // CLT trusts assume that the lifetime beneficiary only has one type of distribution
    const lifetimeBeneficiaryAccessParam = getNodes(
      lifetimeBeneficiary?.accessParameters
    )[0];

    const hasAccessToTrust = !!lifetimeBeneficiaryAccessParam;

    let leadBeneficiary = defaultCharitableBeneficiary();
    if (lifetimeBeneficiary && hasAccessToTrust) {
      const charitableBeneficiaryType: 'unitrust' | 'annuity' =
        validUnitrustTypesV2.includes(lifetimeBeneficiaryAccessParam.kind)
          ? 'unitrust'
          : 'annuity';

      leadBeneficiary = {
        ...beneficiaryFragmentToFormBeneficiary(lifetimeBeneficiary),
        // infer the tab from the distribution kind
        charitableBeneficiaryType,
      };
    }

    return {
      leadBeneficiary,
      charitableBeneficiaryType:
        leadBeneficiary.charitableBeneficiaryType ?? 'annuity',
      ...getListOfFormBeneficiaries(
        data.node.subtype.remainderBeneficiaries ?? [],
        'remainderBeneficiaries'
      ),
    };
  }

  if (data.node.subtype.__typename === 'CRTTrust') {
    const lifetimeBeneficiaries = data.node.subtype.lifetimeBeneficiaries ?? [];

    const incomeBeneficiaries = lifetimeBeneficiaries.map(
      (lifetimeBeneficiary) => {
        const accessParams = getNodes(lifetimeBeneficiary.accessParameters);

        if (accessParams.length > 1) {
          diagnostics.error(
            'Beneficiary count assumption violation',
            new Error(
              'CRT lifetime beneficiary has more than one distribution'
            ),
            {
              beneficiaryId: lifetimeBeneficiary.id,
            }
          );
        }

        // CRT trusts assume that the income beneficiary only has one type of distribution
        const lifetimeBeneficiaryAccessParam = accessParams[0];
        const hasAccessToTrust = !!lifetimeBeneficiaryAccessParam;

        if (hasAccessToTrust) {
          const charitableBeneficiaryType: 'unitrust' | 'annuity' =
            validUnitrustTypesV2.includes(lifetimeBeneficiaryAccessParam.kind)
              ? 'unitrust'
              : 'annuity';
          return {
            ...beneficiaryFragmentToFormBeneficiary(lifetimeBeneficiary),
            charitableBeneficiaryType,
          };
        }

        return defaultCharitableBeneficiary();
      }
    );

    const resolvedIncomeBeneficiaries =
      incomeBeneficiaries.length > 0
        ? incomeBeneficiaries
        : [defaultCharitableBeneficiary()];

    const charitableBeneficiaryType =
      resolvedIncomeBeneficiaries[0]?.charitableBeneficiaryType ?? 'annuity';

    return {
      incomeBeneficiaries: resolvedIncomeBeneficiaries,
      charitableBeneficiaryType,
      ...getListOfFormBeneficiaries(
        data.node.subtype.remainderBeneficiaries ?? [],
        'remainderBeneficiaries'
      ),
    };
  }
  if (data.node.subtype.__typename === 'ILITTrust') {
    return {
      ...getListOfFormBeneficiaries(
        data.node.subtype.beneficiaries ?? [],
        'beneficiaries'
      ),
    };
  }

  if (
    data.node.subtype.__typename === 'IrrevocableTrust' ||
    data.node.subtype.__typename === 'RevocableTrust' ||
    data.node.subtype.__typename === 'GRATTrust' ||
    data.node.subtype.__typename === 'QPRTTrust' ||
    data.node.subtype.__typename === 'DonorAdvisedFund' ||
    data.node.subtype.__typename === 'PrivateFoundation' ||
    data.node.subtype.__typename === 'CustodialPersonalAccount' ||
    data.node.subtype.__typename === 'QualifiedTuitionPersonalAccount' ||
    data.node.subtype.__typename === 'RetirementPersonalAccount' ||
    data.node.subtype.__typename === 'IndividualPersonalAccount' ||
    data.node.subtype.__typename === 'JointPersonalAccount'
  ) {
    return {
      ...getListOfFormBeneficiaries(
        data.node.subtype.beneficiaries ?? [],
        'beneficiaries'
      ),
    };
  }

  throw new Error(`Unexpected subtype ${data.node.subtype.__typename}`);
}

export async function beneficiariesDataFetcher(
  apolloClient: ApolloClient<object>,
  nodeId: string
): Promise<EntityBeneficiariesSubformShape> {
  const { data, error } = await apolloClient.query<BeneficiariesQuery>({
    query: BeneficiariesDocument,
    variables: {
      nodeId: nodeId,
    },
  });

  if (error) {
    diagnostics.error('Could not fetch data for beneficiaries form', error);

    throw error;
  }

  return dataToForm(data);
}

export function getBeneficiariesVariant(
  type: EntityType,
  namespace: EntityBeneficiariesFormNamespace
): EntityBeneficiariesFormVariant {
  if (type === 'grat') {
    return 'percentOnly';
  }

  if (namespace === 'leadBeneficiarySubform') {
    return 'leadBeneficiary';
  }

  if (namespace === 'incomeBeneficiariesSubform') {
    return 'incomeBeneficiaries';
  }

  const accountEntityTypes: EntityType[] = [
    'custodial-account',
    'qualified-tuition-account',
    'retirement-account',
    'joint-account',
    'individual-account',
  ];
  if (includes(accountEntityTypes, type)) {
    return 'personalAccount';
  }

  const nonTrustCharitableEntityTypes: EntityType[] = [
    'daf',
    'private-foundation',
  ];
  if (includes(nonTrustCharitableEntityTypes, type)) {
    return 'nonTrustCharitableEntity';
  }

  return 'default';
}

function getLevelIndex(level?: BeneficiaryLevel | null) {
  if (!level) {
    return 4;
  }

  switch (level) {
    case BeneficiaryLevel.Primary:
      return 0;
    case BeneficiaryLevel.Secondary:
      return 1;
    case BeneficiaryLevel.Tertiary:
      return 2;
    case BeneficiaryLevel.Other:
      return 3;
    default:
      throw new UnreachableError({
        case: level,
        message: `Unrecognized beneficiary level ${level}`,
      });
  }
}

export function sortBeneficiariesByLevel<
  T extends { level?: BeneficiaryLevel | null },
>(beneficiaries: T[]) {
  return orderBy(beneficiaries, (b) => getLevelIndex(b.level), ['asc']);
}

export function isCharitableBeneficiarySubform(
  variant: EntityBeneficiariesFormVariant
): boolean {
  return variant === 'leadBeneficiary' || variant === 'incomeBeneficiaries';
}
